<template>
    <v-card class="alert-form-card d-flex flex-column">
        <v-card-title class="flex-shrink-0">
            <v-btn small icon @click="close" color="primary" class="ml-n3 mr-2">
                <v-icon>mdi-close</v-icon>
            </v-btn>
            <h2 class="font-weight-regular">
                {{ createForm ? 'Create Alert' : 'Edit Alert' }}
            </h2>
        </v-card-title>
        <v-divider class="ml-4 mr-5 flex-shrink-0" />
        <v-card-text class="alert-form-card__scroll flex-grow-1 pt-4">
            <v-form ref="form" v-model="valid">
                <v-row>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="alertType"
                            :items="alertTypeOptions"
                            label="Alert Type *"
                            :rules="[rules.required]"
                            @change="onAlertTypeChange"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="classification"
                            :items="classificationOptions"
                            label="Classification *"
                            :rules="[rules.required]"
                        ></v-select>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-textarea
                            v-model="alertMessage"
                            label="Alert Message *"
                            :rules="[rules.required]"
                            rows="4"
                            auto-grow
                        ></v-textarea>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-autocomplete
                            v-model="selectedProcessId"
                            :items="processesWithCoordinators"
                            item-text="name"
                            item-value="id"
                            label="Responsible Process"
                            prepend-icon="mdi-cog-outline"
                            clearable
                            hide-details
                            :loading="loadingProcesses"
                        />
                    </v-col>
                </v-row>

                <!-- Show partNumbers selector for: edit, remove, or editAssembly -->
                <v-row
                    v-if="
                        workOrder.partNumbers &&
                            Object.keys(workOrder.partNumbers).length > 0 &&
                            (alertType === 'edit' ||
                                alertType === 'remove' ||
                                alertType === 'editAssembly')
                    "
                >
                    <v-col cols="12">
                        <v-select
                            v-model="selectedPartNumbers"
                            :items="partNumberOptions"
                            label="Selected Part Numbers *"
                            :rules="partNumberSelectRules"
                            multiple
                            chips
                            deletable-chips
                        >
                            <template
                                v-if="alertType === 'editAssembly'"
                                v-slot:item="{ parent, item, attrs, on }"
                            >
                                <v-list-item
                                    v-if="!item.hasProduction"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-list-item-action @click.stop>
                                        <v-simple-checkbox
                                            :value="attrs.inputValue"
                                            :ripple="false"
                                            color="primary"
                                            @input="
                                                () =>
                                                    parent.$emit('select', item)
                                            "
                                        />
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>{{
                                            item.text
                                        }}</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-list-item
                                    v-else
                                    v-bind="editAssemblyBlockedAttrs(attrs)"
                                    v-on="editAssemblyBlockedOn(on)"
                                    class="edit-assembly-pn-row--blocked"
                                >
                                    <v-list-item-action
                                        class="ma-0 mr-3"
                                        @click.stop.prevent
                                    >
                                        <div class="d-flex align-center">
                                            <v-simple-checkbox
                                                disabled
                                                :value="false"
                                                :ripple="false"
                                            />
                                            <!-- v-icon often omits native title; span + v-tooltip for hint -->
                                            <v-tooltip
                                                bottom
                                                max-width="280"
                                                :z-index="10020"
                                                :open-delay="80"
                                            >
                                                <template
                                                    v-slot:activator="{
                                                        on,
                                                        attrs,
                                                    }"
                                                >
                                                    <span
                                                        class="ml-1 d-inline-flex align-center edit-assembly-pn-hint-activator"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                        :title="
                                                            editAssemblyProductionTooltip
                                                        "
                                                        tabindex="0"
                                                        role="img"
                                                        :aria-label="
                                                            editAssemblyProductionTooltip
                                                        "
                                                    >
                                                        <v-icon
                                                            small
                                                            color="grey darken-1"
                                                        >
                                                            mdi-information-outline
                                                        </v-icon>
                                                    </span>
                                                </template>
                                                <span>{{
                                                    editAssemblyProductionTooltip
                                                }}</span>
                                            </v-tooltip>
                                        </div>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title
                                            class="text--disabled"
                                        >
                                            {{ item.text }}
                                        </v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-select>
                    </v-col>
                </v-row>

                <!-- Remove old checkboxes -->
                <v-row v-if="false">
                    <v-col
                        cols="12"
                        md="6"
                        v-if="
                            workOrder.assemblyWorkOrder && alertType === 'add'
                        "
                    >
                        <v-checkbox
                            v-model="addAssemblyComponents"
                            label="Add Assembly Components"
                            @change="onAddAssemblyChange"
                        ></v-checkbox>
                    </v-col>
                    <v-col
                        cols="12"
                        md="6"
                        v-if="
                            workOrder.assemblyWorkOrder &&
                                alertType === 'remove'
                        "
                    >
                        <v-checkbox
                            v-model="removeAssemblyComponents"
                            label="Remove Assembly Components"
                            @change="onRemoveAssemblyChange"
                        ></v-checkbox>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions class="flex-shrink-0">
            <v-spacer />
            <v-btn
                :loading="loading"
                color="primary"
                rounded
                class="mt-n2 mb-3 mx-2"
                :disabled="!valid || loading"
                @click="saveAlert"
            >
                {{ createForm ? 'CREATE ALERT' : 'UPDATE ALERT' }}
            </v-btn>
        </v-card-actions>
        <v-snackbar
            v-model="successSnackbar"
            color="success"
            timeout="4000"
            top
        >
            {{ successMessage }}
        </v-snackbar>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'

export default {
    name: 'AlertForm',
    props: {
        workOrder: {
            type: Object,
            required: true,
        },
        alert: {
            type: Object,
            default: () => null,
        },
        createForm: {
            type: Boolean,
            default: () => true,
        },
    },
    data: () => ({
        valid: false,
        loading: false,
        loadingProcesses: false,
        successSnackbar: false,
        successMessage: '',
        alertType: null,
        classification: null,
        alertMessage: '',
        selectedPartNumbers: [],
        selectedProcessId: null,
        addAssemblyComponents: false,
        removeAssemblyComponents: false,
        processes: [],
        alertTypeOptions: [
            { text: 'Add', value: 'add' },
            { text: 'Remove', value: 'remove' },
            { text: 'Edit', value: 'edit' },
        ],
        classificationOptions: [
            { text: 'Major Modification', value: 'major modification' },
            { text: 'Minor Modification', value: 'minor modification' },
        ],
        rules: {
            required: v => !!v || 'This field is required',
        },
        editAssemblyProductionTooltip:
            'This part number cannot be selected because production has already been recorded for it.',
    }),
    computed: {
        partNumberSelectRules() {
            return [
                () =>
                    this.selectedPartNumbers.length > 0 ||
                    'At least one part number must be selected',
                () => {
                    if (this.alertType !== 'editAssembly') return true
                    const invalid = this.selectedPartNumbers.some(id =>
                        this.partNumberLineHasProduction(id)
                    )
                    return (
                        !invalid ||
                        'Cannot select part numbers that already have production recorded'
                    )
                },
            ]
        },
        partNumberOptions() {
            if (!this.workOrder.partNumbers) return []
            return Object.keys(this.workOrder.partNumbers)
                .map(key => {
                    const hasProduction = this.partNumberLineHasProduction(key)
                    return {
                        text: this.workOrder.partNumbers[key].partNumber || key,
                        value: key,
                        hasProduction,
                        disabled:
                            this.alertType === 'editAssembly' && hasProduction,
                    }
                })
                .sort((a, b) => {
                    const last4DigitsA = parseInt(String(a.text).slice(-4), 10)
                    const last4DigitsB = parseInt(String(b.text).slice(-4), 10)
                    return last4DigitsA - last4DigitsB
                })
        },
        processesWithCoordinators() {
            return this.processes.filter(
                p => Array.isArray(p.coordinators) && p.coordinators.length > 0
            )
        },
    },
    async mounted() {
        if (this.workOrder.assemblyWorkOrder) {
            this.alertTypeOptions.push({
                text: 'Edit Assembly Items',
                value: 'editAssembly',
            })
        }
        if (!this.createForm && this.alert) {
            this.loadAlertData()
        }
        await this.loadProcesses()
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        partNumberLineHasProduction(partNumberKey) {
            const part = this.workOrder.partNumbers?.[partNumberKey]
            return Array.isArray(part?.production) && part.production.length > 0
        },
        editAssemblyBlockedAttrs(attrs) {
            if (!attrs || typeof attrs !== 'object') return attrs
            const rest = { ...attrs }
            delete rest.disabled
            return { ...rest, 'aria-disabled': 'true' }
        },
        editAssemblyBlockedOn(on) {
            if (!on || typeof on !== 'object') return on
            return {
                ...on,
                click: e => {
                    e.preventDefault()
                    e.stopPropagation()
                },
            }
        },
        async loadProcesses() {
            try {
                this.loadingProcesses = true
                const {
                    data: { settings },
                } = await API.getSettings()
                const processesSetting = settings.find(
                    s => s.name && s.name.toLowerCase() === 'processes'
                )
                this.processes = processesSetting?.processes || []
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loadingProcesses = false
            }
        },
        loadAlertData() {
            this.alertType = this.alert.alertType
            this.classification = this.alert.classification
            this.alertMessage = this.alert.alertMessage
            this.selectedProcessId = this.alert.processId || null
            // Extract IDs from selectedPartNumbers array of objects
            const selectedPartNumbersData = this.alert.selectedPartNumbers || []
            this.selectedPartNumbers = selectedPartNumbersData.map(
                item => item.id
            )
            if (this.alertType === 'editAssembly') {
                this.selectedPartNumbers = this.selectedPartNumbers.filter(
                    id => !this.partNumberLineHasProduction(id)
                )
            }
            this.addAssemblyComponents =
                this.alert.addAssemblyComponents || false
            this.removeAssemblyComponents =
                this.alert.removeAssemblyComponents || false
        },
        onAlertTypeChange() {
            // Reset dependent fields when alert type changes
            this.selectedPartNumbers = []
            this.addAssemblyComponents = false
            this.removeAssemblyComponents = false
        },
        onAddAssemblyChange() {
            if (this.addAssemblyComponents) {
                this.removeAssemblyComponents = false
                // Reset selected part numbers when enabling add assembly components
                this.selectedPartNumbers = []
            } else {
                this.selectedPartNumbers = []
            }
        },
        onRemoveAssemblyChange() {
            if (this.removeAssemblyComponents) {
                this.addAssemblyComponents = false
                // Reset selected part numbers when enabling remove assembly components
                this.selectedPartNumbers = []
            } else {
                this.selectedPartNumbers = []
            }
        },
        async saveAlert() {
            if (!this.$refs.form.validate()) {
                return
            }

            try {
                this.loading = true
                // Transform selectedPartNumbers from array of IDs to array of objects
                const selectedPartNumbersData = this.selectedPartNumbers.map(
                    key => {
                        const partNumber = this.workOrder.partNumbers[key]
                        return {
                            id: key,
                            partNumber: partNumber?.partNumber || key,
                        }
                    }
                )

                const alertData = {
                    workOrderId: this.workOrder.id,
                    alertType: this.alertType,
                    classification: this.classification,
                    alertMessage: this.alertMessage,
                    selectedPartNumbers: selectedPartNumbersData,
                    processId: this.selectedProcessId || undefined,
                }

                if (this.createForm) {
                    const createdAlert = await API.createAlert(alertData)
                    this.$emit('reload')
                    if (createdAlert?.currentStep === 'pm_designer') {
                        this.successMessage =
                            'Alert created and forwarded to PM/Designer'
                        this.successSnackbar = true
                        // Keep dialog open briefly so the snackbar is visible
                        await new Promise(resolve => setTimeout(resolve, 2500))
                    }
                    this.$emit('close')
                } else {
                    // Update existing alert
                    await API.updateAlert(
                        this.workOrder.id,
                        this.alert.id,
                        alertData
                    )
                    this.$emit('reload')
                    this.$emit('close')
                }
            } catch (error) {
                const isNotificationError =
                    error?.response?.data?.name === 'NotificationError'
                this.setErrorItems({
                    source: this.$options.name,
                    message: isNotificationError
                        ? 'An error occurred while sending the notification, please try again'
                        : error.message,
                })
            } finally {
                this.loading = false
            }
        },
        close() {
            this.$emit('close')
        },
    },
}
</script>

<style scoped>
.alert-form-card {
    max-height: min(85vh, 720px);
    min-height: 0;
}
.alert-form-card__scroll {
    overflow-y: auto;
    min-height: 0;
}
.edit-assembly-pn-row--blocked {
    opacity: 0.72;
    pointer-events: auto;
}
.edit-assembly-pn-hint-activator {
    cursor: help;
    pointer-events: auto;
}
</style>
